"use client"

/** Public, non-secret URL of the server-side lead endpoint. */
// Never simulate success: report that online submission is not yet connected.
/* Honeypot — hidden from people and assistive tech */

import {
  useEffect,
  useRef,
  useState,
  type FormEvent,
  type ReactNode,
} from "react"
import { Link, useRouter, useT, type L10n } from "../lib/router"
import { services } from "../content/services"
import { countriesByLocale } from "../content/countries"
import { validateLead, type LeadErrors, type LeadInput } from "../lib/lead"
import { leadAction } from "../lib/recaptcha-actions"
import { RecaptchaScript, getRecaptchaToken } from "../components/recaptcha"
import {
  Container,
  Icon,
  PageHeader,
  SITE,
  Button,
} from "../components/ui"
const ENDPOINT =
  (typeof process !== "undefined" && process.env?.NEXT_PUBLIC_LEAD_ENDPOINT) ||
  "/api/lead"

const msg: Record<NonNullable<LeadErrors[keyof LeadErrors]>, L10n> = {
  required: { en: "This field is required.", ar: "هذا الحقل مطلوب." },
  invalid: { en: "Please check this value.", ar: "يرجى التحقق من هذه القيمة." },
  tooLong: { en: "This is too long.", ar: "النص أطول من المسموح." },
}
const specific: Partial<Record<keyof LeadInput, L10n>> = {
  email: {
    en: "Enter a valid email, e.g. name@company.com.",
    ar: "أدخل بريدًا صحيحًا، مثل name@company.com.",
  },
  phone: {
    en: "Use digits only, with an optional country code.",
    ar: "استخدم الأرقام فقط مع رمز الدولة اختياريًا.",
  },
  description: {
    en: "Please add a few more details (at least 10 characters).",
    ar: "يرجى إضافة تفاصيل أكثر (10 أحرف على الأقل).",
  },
  consent: {
    en: "Please confirm so we can contact you.",
    ar: "يرجى الموافقة حتى نتمكن من التواصل معك.",
  },
}

type Status = { state: "idle" | "sending" | "success" | "error"; code?: string }

function Field({
  id,
  label,
  error,
  hint,
  optional,
  children,
}: {
  id: string
  label: string
  error?: string
  hint?: string
  optional?: boolean
  children: ReactNode
}) {
  const t = useT()
  return (
    <div>
      <label
        htmlFor={id}
        className="flex items-baseline justify-between text-base font-medium text-ink"
      >
        {label}
        {optional && (
          <span className="text-sm font-normal text-slate">
            {t({ en: "Optional", ar: "اختياري" })}
          </span>
        )}
      </label>
      {hint && (
        <p id={`${id}-hint`} className="mt-1 text-sm text-slate">
          {hint}
        </p>
      )}
      <div className="mt-2">{children}</div>
      {error && (
        <p
          id={`${id}-error`}
          className="mt-2 text-sm font-medium text-[#B42318]"
        >
          {error}
        </p>
      )}
    </div>
  )
}

const input =
  "block w-full rounded-r8 border border-slate bg-white px-4 py-3 text-base text-ink placeholder:text-slate focus:border-blue aria-[invalid=true]:border-[#B42318]"

export function LeadForm({ kind }: { kind: "contact" | "project" }) {
  const t = useT()
  const { route } = useRouter()
  const [v, setV] = useState<LeadInput>(() => ({
    kind,
    name: "",
    company: "",
    email: "",
    phone: "",
    country: "",
    service:
      typeof window !== "undefined"
        ? (new URLSearchParams(window.location.search).get("service") ?? "")
        : "",
    description: "",
    budget: "",
    contactMethod: "",
    consent: false,
    website: "",
    locale: route.locale,
    sourcePage: typeof window !== "undefined" ? window.location.pathname : "",
  }))

  useEffect(() => {
    if (typeof window !== "undefined") {
      const s = new URLSearchParams(window.location.search).get("service")
      if (s) {
        setV((prev) => ({
          ...prev,
          service: s,
          sourcePage: window.location.pathname,
        }))
      }
    }
  }, [])
  const [errors, setErrors] = useState<LeadErrors>({})
  const [status, setStatus] = useState<Status>({ state: "idle" })
  const summaryRef = useRef<HTMLDivElement>(null)
  const submitting = useRef(false)

  const set = <K extends keyof LeadInput>(k: K, val: LeadInput[K]) => {
    setV((p) => ({ ...p, [k]: val }))
    if (errors[k]) setErrors((e) => ({ ...e, [k]: undefined }))
  }
  const err = (k: keyof LeadInput) =>
    errors[k]
      ? t(
          specific[k] && errors[k] !== "tooLong"
            ? specific[k]!
            : msg[errors[k]!],
        )
      : undefined
  const a11y = (k: keyof LeadInput) => ({
    id: k,
    name: k,
    "aria-invalid": !!errors[k],
    "aria-describedby": errors[k] ? `${k}-error` : undefined,
  })

  async function submit(e: FormEvent) {
    e.preventDefault()
    if (submitting.current) return
    const found = validateLead(v)
    setErrors(found)
    if (Object.keys(found).length) {
      document.getElementById(Object.keys(found)[0])?.focus()
      return
    }
    if (!ENDPOINT) {
      setStatus({ state: "error", code: "not_configured" })
      requestAnimationFrame(() => summaryRef.current?.focus())
      return
    }
    submitting.current = true
    setStatus({ state: "sending" })
    try {
      let recaptchaToken: string
      try {
        recaptchaToken = await getRecaptchaToken(leadAction(kind))
      } catch (error) {
        setStatus({ state: "error", code: error instanceof Error ? error.message : "captcha_unavailable" })
        return
      }
      const search = typeof window !== "undefined" ? window.location.search : ""
      const searchParams = new URLSearchParams(search)
      const utm = Object.fromEntries(
        [...searchParams].filter(([k]) => k.startsWith("utm_")),
      )
      const res = await fetch(ENDPOINT, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ ...v, kind, locale: route.locale, utm, recaptchaToken }),
      })
      const data = await res.json().catch(() => ({}))
      if (res.ok && data.ok) setStatus({ state: "success" })
      else {
        if (data.fields) setErrors(data.fields)
        setStatus({ state: "error", code: data.error ?? "unknown" })
      }
    } catch {
      setStatus({ state: "error", code: "network" })
    } finally {
      submitting.current = false
      requestAnimationFrame(() => summaryRef.current?.focus())
    }
  }

  if (status.state === "success") {
    return (
      <div
        ref={summaryRef}
        tabIndex={-1}
        role="status"
        className="rounded-r24 border border-line bg-ice p-10"
      >
        <span className="inline-flex size-12 items-center justify-center rounded-r8 bg-blue text-white">
          <Icon name="check" />
        </span>
        <h2 className="mt-6 text-2xl font-medium text-ink">
          {t({
            en: "Thank you — we received your message.",
            ar: "شكرًا لك، وصلتنا رسالتك.",
          })}
        </h2>
        <p className="mt-3 text-base text-ink">
          {t({
            en: "A member of our team will review it and reply by your preferred channel.",
            ar: "سيراجعها أحد أعضاء فريقنا ويرد عليك عبر وسيلة التواصل المفضلة لديك.",
          })}
        </p>
        <div className="mt-8">
          <Button to="/services" variant="secondary">
            {t({ en: "Explore services", ar: "استكشف الخدمات" })}
          </Button>
        </div>
      </div>
    )
  }

  const errorText: Record<string, L10n> = {
    captcha: {
      en: "We could not verify this submission. Please try again or email us.",
      ar: "تعذر التحقق من هذا الطلب. يرجى المحاولة مجددًا أو مراسلتنا عبر البريد.",
    },
    captcha_unavailable: {
      en: "Verification is temporarily unavailable. Check your connection and try again, or email us.",
      ar: "التحقق غير متاح مؤقتًا. تحقق من اتصالك وحاول مجددًا، أو راسلنا عبر البريد.",
    },
    not_configured: {
      en: "Online submission is not connected yet. Please email us directly — your message will reach the same team.",
      ar: "الإرسال الإلكتروني غير مفعّل بعد. يرجى مراسلتنا مباشرة عبر البريد، وستصل رسالتك إلى الفريق نفسه.",
    },
    rate_limited: {
      en: "Too many attempts. Please wait a few minutes and try again.",
      ar: "محاولات كثيرة. يرجى الانتظار بضع دقائق ثم المحاولة مجددًا.",
    },
    validation: {
      en: "Some fields need attention.",
      ar: "بعض الحقول تحتاج إلى مراجعة.",
    },
    network: {
      en: "We could not send your message. Check your connection and try again.",
      ar: "تعذر إرسال رسالتك. تحقق من اتصالك وحاول مجددًا.",
    },
  }
  const mailto = `mailto:${SITE.email}?subject=${encodeURIComponent((kind === "project" ? "Project inquiry" : "Website inquiry") + (v.company ? ` — ${v.company}` : ""))}&body=${encodeURIComponent(`${v.name}\n${v.company}\n${v.phone}\n\n${v.description}`)}`

  return (
    <form
      noValidate
      onSubmit={submit}
      className="space-y-6"
      aria-describedby="form-status"
    >
      <RecaptchaScript />
      <div id="form-status" ref={summaryRef} tabIndex={-1} aria-live="polite">
        {status.state === "error" && (
          <div
            role="alert"
            className="rounded-r16 border border-[#F4C7C3] bg-[#FEF3F2] p-5 text-base text-[#7A271A]"
          >
            <p className="font-medium">
              {t(
                errorText[status.code ?? ""] ?? {
                  en: "Something went wrong on our side. Please try again or email us.",
                  ar: "حدث خطأ من جهتنا. يرجى المحاولة مجددًا أو مراسلتنا عبر البريد.",
                },
              )}
            </p>
            <a
              href={mailto}
              className="mt-2 inline-flex items-center gap-2 font-medium text-blue underline"
              style={{ direction: "ltr" }}
            >
              <Icon name="mail" className="size-4" />
              {SITE.email}
            </a>
          </div>
        )}
      </div>

      <div className="grid gap-6 sm:grid-cols-2">
        <Field
          id="name"
          label={t({ en: "Full name", ar: "الاسم الكامل" })}
          error={err("name")}
        >
          <input
            {...a11y("name")}
            autoComplete="name"
            className={input}
            value={v.name}
            onChange={(e) => set("name", e.target.value)}
          />
        </Field>
        <Field
          id="company"
          label={t({ en: "Company", ar: "الشركة" })}
          optional={kind === "contact"}
          error={err("company")}
        >
          <input
            {...a11y("company")}
            autoComplete="organization"
            className={input}
            value={v.company}
            onChange={(e) => set("company", e.target.value)}
          />
        </Field>
        <Field
          id="email"
          label={t({ en: "Business email", ar: "البريد الإلكتروني للعمل" })}
          error={err("email")}
        >
          <input
            {...a11y("email")}
            type="email"
            dir="ltr"
            autoComplete="email"
            className={input}
            value={v.email}
            onChange={(e) => set("email", e.target.value)}
          />
        </Field>
        <Field
          id="phone"
          label={t({ en: "Phone / WhatsApp", ar: "الهاتف / واتساب" })}
          optional
          error={err("phone")}
        >
          <input
            {...a11y("phone")}
            type="tel"
            dir="ltr"
            autoComplete="tel"
            className={input}
            value={v.phone}
            onChange={(e) => set("phone", e.target.value)}
          />
        </Field>
      </div>

      {kind === "project" && (
        <div className="grid gap-6 sm:grid-cols-2">
          <Field
            id="service"
            label={t({ en: "Interested in", ar: "الخدمة المطلوبة" })}
            error={err("service")}
          >
            <select
              {...a11y("service")}
              className={input}
              value={v.service}
              onChange={(e) => set("service", e.target.value)}
            >
              <option value="">
                {t({ en: "Select a service", ar: "اختر خدمة" })}
              </option>
              {services.map((s) => (
                <option key={s.slug} value={s.slug}>
                  {t(s.title)}
                </option>
              ))}
              <option value="not-sure">
                {t({
                  en: "Not sure yet — need advice",
                  ar: "لست متأكدًا، أحتاج إلى استشارة",
                })}
              </option>
            </select>
          </Field>
          <Field
            id="country"
            label={t({ en: "Country", ar: "الدولة" })}
            optional
          >
            <select
              {...a11y("country")}
              autoComplete="country-name"
              className={input}
              value={v.country}
              onChange={(e) => set("country", e.target.value)}
            >
              <option value="">
                {t({ en: "Select a country", ar: "اختر الدولة" })}
              </option>
              {countriesByLocale[route.locale].map((country) => (
                <option key={country.code} value={country.en}>
                  {t(country)}
                </option>
              ))}
            </select>
          </Field>
        </div>
      )}

      <Field
        id="description"
        label={
          kind === "project"
            ? t({ en: "Tell us about the project", ar: "حدّثنا عن المشروع" })
            : t({ en: "How can we help?", ar: "كيف يمكننا مساعدتك؟" })
        }
        hint={
          kind === "project"
            ? t({
                en: "What should change in your business, and what tools do you use today?",
                ar: "ما الذي تريد تغييره في أعمالك، وما الأدوات التي تستخدمها حاليًا؟",
              })
            : undefined
        }
        error={err("description")}
      >
        <textarea
          {...a11y("description")}
          rows={5}
          className={input}
          value={v.description}
          onChange={(e) => set("description", e.target.value)}
        />
      </Field>

      {kind === "project" && (
        <details className="group rounded-r16 border border-line p-5">
          <summary className="flex cursor-pointer list-none items-center justify-between text-base font-medium text-ink [&::-webkit-details-marker]:hidden">
            {t({
              en: "Budget and contact preferences",
              ar: "الميزانية وتفضيلات التواصل",
            })}
            <Icon
              name="plus"
              className="size-5 text-blue transition-transform group-open:rotate-45"
            />
          </summary>
          <div className="mt-5 grid gap-6 sm:grid-cols-2">
            <Field
              id="budget"
              label={t({ en: "Budget range", ar: "نطاق الميزانية" })}
              optional
            >
              <select
                {...a11y("budget")}
                className={input}
                value={v.budget}
                onChange={(e) => set("budget", e.target.value)}
              >
                <option value="">
                  {t({ en: "Prefer to discuss", ar: "أفضّل النقاش" })}
                </option>
                <option value="up-to-1k">
                  {t({ en: "$1,000 or less", ar: "1,000 دولار أو أقل" })}
                </option>
                <option value="1k-5k">
                  {t({ en: "$1,000 – $5,000", ar: "1,000 – 5,000 دولار" })}
                </option>
                <option value="5k-15k">
                  {t({ en: "$5,000 – $15,000", ar: "5,000 – 15,000 دولار" })}
                </option>
                <option value="15k-50k">
                  {t({ en: "$15,000 – $50,000", ar: "15,000 – 50,000 دولار" })}
                </option>
                <option value="50k-plus">
                  {t({ en: "Over $50,000", ar: "أكثر من 50,000 دولار" })}
                </option>
              </select>
            </Field>
            <Field
              id="contactMethod"
              label={t({
                en: "Preferred contact",
                ar: "وسيلة التواصل المفضلة",
              })}
              optional
            >
              <select
                {...a11y("contactMethod")}
                className={input}
                value={v.contactMethod}
                onChange={(e) => set("contactMethod", e.target.value)}
              >
                <option value="">
                  {t({ en: "No preference", ar: "بلا تفضيل" })}
                </option>
                <option value="email">
                  {t({ en: "Email", ar: "البريد الإلكتروني" })}
                </option>
                <option value="phone">
                  {t({ en: "Phone call", ar: "مكالمة هاتفية" })}
                </option>
                <option value="whatsapp">WhatsApp</option>
                <option value="meeting">
                  {t({ en: "Video meeting", ar: "اجتماع مرئي" })}
                </option>
              </select>
            </Field>
          </div>
        </details>
      )}

      {}
      <div
        aria-hidden="true"
        className="absolute -start-[9999px] h-0 w-0 overflow-hidden"
      >
        <label>
          Website
          <input
            tabIndex={-1}
            autoComplete="off"
            value={v.website}
            onChange={(e) => set("website", e.target.value)}
          />
        </label>
      </div>

      <div>
        <label className="flex items-start gap-3 text-base text-ink">
          <input
            {...a11y("consent")}
            type="checkbox"
            className="mt-1.5 size-5 shrink-0 accent-[#023493]"
            checked={v.consent}
            onChange={(e) => set("consent", e.target.checked)}
          />
          <span>
            {t({
              en: "I agree that ORKEST may store these details to respond to my inquiry, as described in the ",
              ar: "أوافق على أن تحتفظ أوركست بهذه البيانات للرد على استفساري، كما هو موضح في ",
            })}
            <Link to="/privacy" className="text-blue underline">
              {t({ en: "privacy notice", ar: "إشعار الخصوصية" })}
            </Link>
            .
          </span>
        </label>
        {errors.consent && (
          <p
            id="consent-error"
            className="mt-2 text-sm font-medium text-[#B42318]"
          >
            {err("consent")}
          </p>
        )}
      </div>

      <p className="text-sm leading-6 text-slate">
        {t({ en: "This form is protected by reCAPTCHA. Google's ", ar: "هذا النموذج محمي بواسطة reCAPTCHA، وتسري عليه " })}
        <a href="https://policies.google.com/privacy" className="text-blue underline">
          {t({ en: "Privacy Policy", ar: "سياسة خصوصية Google" })}
        </a>
        {t({ en: " and ", ar: " و" })}
        <a href="https://policies.google.com/terms" className="text-blue underline">
          {t({ en: "Terms of Service", ar: "شروط الخدمة" })}
        </a>
        {t({ en: " apply.", ar: "." })}
      </p>

      <button
        type="submit"
        disabled={status.state === "sending"}
        className="inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-r8 bg-blue px-6 text-base font-medium text-white hover:bg-ink disabled:opacity-60 sm:w-auto"
      >
        {status.state === "sending"
          ? t({ en: "Sending…", ar: "جارٍ الإرسال…" })
          : kind === "project"
            ? t({ en: "Send project inquiry", ar: "إرسال طلب المشروع" })
            : t({ en: "Send message", ar: "إرسال الرسالة" })}
      </button>
    </form>
  )
}

function Aside({ kind }: { kind: "contact" | "project" }) {
  const t = useT()
  const steps = [
    {
      en: "We read your message and reply with questions or a call slot.",
      ar: "نقرأ رسالتك ونرد بأسئلة أو موعد لمكالمة.",
    },
    {
      en: "A short discovery conversation about your process and goals.",
      ar: "محادثة قصيرة لفهم عمليتك وأهدافك.",
    },
    {
      en: "A written proposal with scope, phases and estimate.",
      ar: "عرض مكتوب يوضح النطاق والمراحل والتقدير.",
    },
  ]
  return (
    <aside className="space-y-8 lg:sticky lg:top-28">
      <div className="rounded-r24 bg-mist p-8">
        <h2 className="text-lg font-medium text-ink">
          {t({ en: "What happens next", ar: "ماذا يحدث بعد ذلك" })}
        </h2>
        <ol className="mt-6 space-y-5">
          {steps.map((s, i) => (
            <li key={i} className="flex gap-4">
              <span className="inline-flex size-8 shrink-0 items-center justify-center rounded-r8 bg-blue text-sm font-medium text-white">
                {i + 1}
              </span>
              <span className="text-base text-slate">{t(s)}</span>
            </li>
          ))}
        </ol>
      </div>
      <div className="rounded-r24 border border-line p-8">
        <h2 className="text-lg font-medium text-ink">
          {t({ en: "Prefer email?", ar: "تفضّل البريد الإلكتروني؟" })}
        </h2>
        <a
          href={`mailto:${SITE.email}`}
          className="mt-4 flex items-center gap-3 text-base font-medium text-blue"
        >
          <Icon name="mail" className="size-5 shrink-0" />
          <bdi dir="ltr" className="min-w-0 [overflow-wrap:anywhere]">
            {SITE.email}
          </bdi>
        </a>
        <p className="mt-3 flex items-center gap-3 text-base text-slate">
          <Icon name="pin" className="size-5" />
          {t(SITE.location)}
        </p>
        {kind === "contact" && (
          <div className="mt-6">
            <Button to="/start-project" variant="secondary">
              {t({ en: "Planning a project?", ar: "تخطط لمشروع؟" })}
            </Button>
          </div>
        )}
      </div>
    </aside>
  )
}

export function Contact({ kind }: { kind: "contact" | "project" }) {
  const t = useT()
  const contact = kind === "contact"
  return (
    <>
      <PageHeader
        crumbs={[
          {
            label: contact
              ? t({ en: "Contact", ar: "تواصل معنا" })
              : t({ en: "Start a project", ar: "ابدأ مشروعك" }),
          },
        ]}
        eyebrow={
          contact
            ? t({ en: "Contact", ar: "تواصل معنا" })
            : t({ en: "Start a project", ar: "ابدأ مشروعك" })
        }
        title={
          contact
            ? t({
                en: "Let\u2019s talk about your business.",
                ar: "لنتحدث عن أعمالك.",
              })
            : t({
                en: "Tell us what you want to change.",
                ar: "أخبرنا بما تريد تغييره.",
              })
        }
        lead={
          contact
            ? t({
                en: "A question, a consultation or an early idea — send a short message and we will reply.",
                ar: "سؤال أو استشارة أو فكرة أولية، أرسل رسالة قصيرة وسنرد عليك.",
              })
            : t({
                en: "A few details help us prepare a useful first conversation. It takes about two minutes.",
                ar: "بعض التفاصيل تساعدنا على الإعداد لمحادثة أولى مفيدة، ولن يستغرق ذلك أكثر من دقيقتين.",
              })
        }
      />
      <section className="bg-white py-16 md:py-24">
        <Container className="grid gap-14 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <LeadForm kind={kind} />
          </div>
          <div className="lg:col-span-5">
            <Aside kind={kind} />
          </div>
        </Container>
      </section>
    </>
  )
}
